<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { usePvpCardGameStore } from '../services/gameService';
import { storeToRefs } from 'pinia';
import Card from './Card.vue';
import { dayjs } from 'element-plus';
import { throttle } from 'lodash';
import ElixirBar from './ElixirBar.vue';
import BattleCard from './BattleCard.vue';
import AspectRatio from './AspectRatio.vue';
const pvpCardGameStore = usePvpCardGameStore()
const gameState = storeToRefs(pvpCardGameStore).gameState;

const myPlayer = computed(() => {
  const sessionId = pvpCardGameStore.gameRoom?.sessionId!;
  return gameState.value?.players[sessionId]!; // ✅ 对象访问，响应式
})

const chessCards = computed(() => {
  return gameState.value?.cards
})
function dirFilter(value: number) {
  if (myPlayer.value.dir === 'red') {
    return 14 - value
  }
  return value
}
const selectCard = ref(0)
function setSelectCard(el: HTMLElement) {
  if (el.dataset.card) {
    if (selectCard.value !== +el.dataset.card) {
      selectCard.value = +el.dataset.card
    }
    else {
      selectCard.value = 0
    }
  }
}
function touchStart(e: TouchEvent) {
  rect()
  e.preventDefault()
  const el = e.target as HTMLElement
  setSelectCard(el)
}
function mouseDown(e: MouseEvent) {
  rect()
  e.preventDefault()
  const el = e.target as HTMLElement
  setSelectCard(el)
}
const gridArea = ref<HTMLDivElement>()
let gridAreaBasicPos = { top: 0, left: 0 }
const gridAreaItemSize = ref({ width: 0, height: 0 })
let oldZoom = '1'
onMounted(() => {
  oldZoom = document.documentElement.style.getPropertyValue('zoom') || '1'
  document.documentElement.style.setProperty('zoom', '1')
  rect()
});


onUnmounted(() => {
  document.documentElement.style.setProperty('zoom', oldZoom)
})

function rect() {
  if (gridArea.value) {
    const rect = gridArea.value.getBoundingClientRect();
    gridAreaBasicPos = { top: rect.top, left: rect.left };
    gridAreaItemSize.value = { width: rect.width / 15, height: rect.height / 15 };
  }
}


const tempChessPos = reactive({ x: 0, y: 0 })
const highlightPos = reactive({ x: 0, y: 0, down: false })
const highlightIndex = computed(() => {
  const index = highlightPos.x + highlightPos.y * 15
  let validIndex = index > 119 && index < 225 ? index : -1
  if (pvpCardGameStore.cardData[selectCard.value]?.specials.some(([key]) => key === 'ghost')) {
    validIndex = index > -1 && index < 225 ? index : -1
  }

  return {
    validIndex,
    index: highlightPos.down ? index : -1,
    valid: index === validIndex
  }
})
function getPointerPos(touch: { pageX: number, pageY: number }) {
  tempChessPos.x = touch.pageX - 10
  tempChessPos.y = touch.pageY - 10
  const x = tempChessPos.x - gridAreaBasicPos.left
  const y = tempChessPos.y - gridAreaBasicPos.top
  const xIndex = Math.floor(x / gridAreaItemSize.value.width)
  const yIndex = Math.floor(y / gridAreaItemSize.value.height)
  highlightPos.x = Math.max(0, Math.min(14, xIndex))
  highlightPos.y = yIndex
  highlightPos.down = true
}

const touchMove = throttle((e: TouchEvent) => {
  if (!selectCard.value) return;
  e.preventDefault();
  getPointerPos(e.touches[0]);
}, 16);

const mouseMove = throttle((e: MouseEvent) => {
  if (!selectCard.value) return;
  e.preventDefault();
  getPointerPos(e);
}, 16);
function touchEnd() {
  if (highlightIndex.value.valid) {
    pvpCardGameStore.useCard(selectCard.value, highlightIndex.value.validIndex)
    selectCard.value = 0
    highlightPos.x = 0
    highlightPos.y = 0
    tempChessPos.x = 0
    tempChessPos.y = 0
  }
  highlightPos.down = false
}


const attackAnimation = computed(() => pvpCardGameStore.attackAnimation);
const attackLines = ref<Array<{ id: number; style: Record<string, string> }>>([]);
let lineId = 0;

watch(attackAnimation, (anim) => {
  if (!anim) return;

  const attacker = gameState.value?.cards[anim.attackerId];
  const target = gameState.value?.cards[anim.targetId];

  if (!attacker || !target) return;

  // 确保坐标计算前 gridArea 已渲染且位置最新
  if (!gridArea.value) return;

  const start = getCardScreenPosition(attacker.x, attacker.y, myPlayer.value.dir);
  const end = getCardScreenPosition(target.x, target.y, myPlayer.value.dir);

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

  const id = ++lineId;

  const style = {
    position: 'absolute',
    left: `${start.x}px`,
    top: `${start.y}px`,
    width: `${length}px`,
    height: '2px',
    background: 'linear-gradient(90deg, transparent,#ff3366,#ffcc00)',
    borderRadius: '1px',
    pointerEvents: 'none',
    zIndex: '10',
    transformOrigin: 'left center',
    transform: `rotate(${angle}deg)`, // ✅ 旋转到位
    opacity: '0',
    animation: 'attack-line-fade 0.3s ease-in forwards' // ✅ 只控制透明度
  };
  attackLines.value.push({
    id,
    style
  });
});



function removeAttackLine(id: number) {
  attackLines.value = attackLines.value.filter((line) => line.id !== id);
}

// 计算单位在屏幕上的中心位置（像素）
function getCardScreenPosition(logicX: number, logicY: number, dir: string) {
  // 先映射 UI 坐标
  const uiX = dir === 'red' ? 14 - logicX : logicX;
  const uiY = dir === 'red' ? 14 - logicY : logicY;

  const size = gridAreaItemSize.value.width;

  return {
    x: (uiX + 0.5) * size,
    y: (uiY + 0.5) * size
  };
}

const gameTime = computed(() => {
  if (pvpCardGameStore.mode === 'standard') {
    const offset = [0, 3, 3, 3.5][pvpCardGameStore.gameStep]
    return dayjs(pvpCardGameStore.gameTime).add(offset, 'minute').toDate().getTime()
  } else if (pvpCardGameStore.mode === 'pve') {
    return dayjs(pvpCardGameStore.gameTime).add(2, 'minute').toDate().getTime()
  }

}
)
const countdownStyle = computed(() => {
  if (pvpCardGameStore.gameStep === 3) {
    return 'color: #ff3366;'
  }
  if (pvpCardGameStore.gameStep === 2) {
    return 'color: #ffcc00;'
  }
  return ''
})

const nextCardId = computed(() => {
  return myPlayer.value?.backupCard[0] ?? 0;
});

const nextCardDetail = computed(() => {
  return pvpCardGameStore.getDetailById(nextCardId.value);
});

// 手牌详情也预计算
const handCardDetails = computed(() => {
  return myPlayer.value?.handCard.map(id => ({
    id,
    detail: pvpCardGameStore.getDetailById(id)
  })) || [];
});


// ====== 替换 2：预计算棋子屏幕位置（替代 CSS calc） ======
const chessPositions = computed(() => {
  if (!chessCards.value || !myPlayer.value || gridAreaItemSize.value.width <= 0) {
    return [];
  }
  const size = gridAreaItemSize.value.width; // 假设宽高相等
  return Object.values(chessCards.value).map(card => {
    const uiX = dirFilter(card.x);
    const uiY = dirFilter(card.y);
    return {
      ...card,
      left: -size / 2,
      top: -size / 2,
      size: size * 2,
      transform: `translate(${uiX * size}px, ${uiY * size}px)`,
      level: pvpCardGameStore.getCardLevel(card),
      icon: pvpCardGameStore.getCardTypeIcon(card)
    };
  });
});

const redName = computed(() => {
  return Object.values(gameState.value?.players).find(player => player.dir === 'red')?.name || ''
})
const blueName = computed(() => {
  return Object.values(gameState.value?.players).find(player => player.dir === 'blue')?.name || ''
})
const showBattleIntro = ref(true)
function handleAnimationEnd(event: AnimationEvent) {
  if (event.target !== event.currentTarget) return;
  showBattleIntro.value = false
}
const showCardDetail = ref(localStorage.getItem('show-card-detail') === 'true')
function changeShowCardDetail() {
  localStorage.setItem('show-card-detail', showCardDetail.value.toString())
}

</script>
<template>
  <div class="battle-wrap" :class="{ 'dead-fire': pvpCardGameStore.gameStep === 3 }" @touchmove="touchMove"
    @touchstart="touchMove" @touchend="touchEnd" @mousedown="mouseMove" @mousemove="mouseMove" @mouseup="touchEnd">
    <!-- 对局时间 -->
    <div class="time-area">
      <div style="width: 72px;"></div>
      <div class="time">
        <el-countdown v-if="myPlayer" format="mm:ss" :value-style="countdownStyle" :value="gameTime" />
      </div>
      <div style="float:right">
        <el-switch style="" @change="changeShowCardDetail" v-model="showCardDetail" inline-prompt active-text="详细模式"
          inactive-text="简洁模式" />
      </div>
    </div>

    <!-- 战斗区域 -->
    <div class="battle-area">

      <aspect-ratio :ratio="1">
        <div class="grid-area  " ref="gridArea">
          <!-- 纯网格（无 highlight 判断） -->
          <div class="grid-item" v-for="value of 225" :key="value"></div>

          <!-- 棋子：使用预计算的绝对位置 -->
          <div class="chess-area" v-if="myPlayer">
            <div class="chess" v-for="item of chessPositions" :data-card-id="item.id" :key="item.id" :style="{
              transform: item.transform,
              width: item.size + 'px',
              height: item.size + 'px',
              top: item.top + 'px',
              left: item.left + 'px',
            }">
              <el-progress :status="item.dir === myPlayer.dir ? '' : 'exception'" type="circle"
                :percentage="item.hp * 100 / item.health">
                <div class="progress-name">
                  {{ item.name }}
                </div>
                <div class="card-level">{{ item.level }}</div>
                <div class="card-icon" v-show="showCardDetail">{{ item.icon }}</div>
              </el-progress>
            </div>
          </div>

          <!-- 新增高亮层（绝对定位） -->
          <div v-if="highlightIndex.index > -1" :class="{ valid: highlightIndex.valid }" class="grid-highlight" :style="{
            left: highlightPos.x * gridAreaItemSize.width + 'px',
            top: highlightPos.y * gridAreaItemSize.height + 'px',
            width: gridAreaItemSize.width + 'px',
            height: gridAreaItemSize.height + 'px',
          }">
          </div>

          <!-- 攻击线容器（保持不变） -->
          <div class="attack-line-container">
            <div v-for="anim in attackLines" :key="anim.id" class="attack-line" :style="anim.style"
              @animationend="removeAttackLine(anim.id)" />
          </div>
          <div class="effect-container"></div>
        </div>
      </aspect-ratio>

    </div>
    <!-- 手牌区域 -->
    <div v-if="myPlayer">
      <div class="hand-card-area">
        <!-- 下一张牌 -->
        <div class="next-card">
          <div class="card">
            <Card :card="nextCardDetail" />
          </div>
        </div>
        <!-- 当前手牌 -->
        <div class="current-card" @touchstart="touchStart" @mousedown="mouseDown">
          <template v-if="showCardDetail">
            <div class="card" :class="{ 'select': selectCard === item.id }" :data-card="item.id"
              v-for="item of handCardDetails" :key="item.id">
              <BattleCard :card="item.detail" />
            </div>
          </template>
          <template v-else>
            <div class="card" :class="{ 'select': selectCard === item.id }" :data-card="item.id"
              v-for="item of handCardDetails" :key="item.id">
              <Card :card="item.detail"></Card>
            </div>
          </template>

        </div>
        <!-- 当前能量 -->
        <!-- <div class="energy">
          <div>{{ Math.floor(myPlayer.mana) }}</div>
          <div>🪙</div>
        </div> -->
      </div>
      <ElixirBar :value="myPlayer.mana"></ElixirBar>
    </div>
    <!-- 临时棋子 -->
    <div class="temp-chess" :class="{ 'show': selectCard }" :style="{
      '--y': tempChessPos.y + 'px',
      '--x': tempChessPos.x + 'px'
    }">
      {{ (pvpCardGameStore.getDetailById(selectCard)?.name ?? '').slice(0, 1) }}
    </div>
    <div class="battle-intro" @animationend="handleAnimationEnd" v-if="showBattleIntro">
      <div class="text zhanhun" style="font-size: 36px;">{{ redName }}</div>
      <div class="text duijue" style="font-size: 36px;">{{ blueName }}</div>
      <div class="text vs">VS</div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.battle-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;

  &.dead-fire {
    animation: deadFire 1s ease-in-out infinite;
  }
}

@keyframes deadFire {
  0% {
    box-shadow: 0 0 0px 0 transparent inset;
  }

  50% {

    box-shadow: 0 0 40px 0 #ff3366 inset;
  }

  100% {
    box-shadow: 0 0 0px 0 transparent inset;

  }
}

.time-area {
  height: 30px;
  text-align: center;
  line-height: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.battle-area {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;

  .grid-area {
    position: relative;
    width: 100%;
    // aspect-ratio: 1;
    display: grid;
    grid-template-columns: repeat(15, 1fr);
    grid-template-rows: repeat(15, 1fr);

    .grid-item {
      border: 0 solid rgba(255, 255, 255, 0.3);
      border-left-width: 1px;
      border-top-width: 1px;
      background-color: rgba(255, 200, 200, 0.2);
      pointer-events: none;

      &.highlight {
        box-shadow: 0 0 10px 0 #ffff00 inset;
      }

      &:nth-of-type(15n) {
        border-right-width: 1px;
      }

      &:nth-of-type(n+211) {
        border-bottom-width: 1px;
      }

      // &:nth-of-type(n+22) {
      //   background-color: rgba(255, 255, 220, 0.2);
      // }
      &:nth-of-type(n+121) {
        background-color: rgba(200, 220, 255, 0.2);
      }

    }

    .chess-area {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;

      .chess {
        position: absolute;
        top: 0;
        left: 0;
        padding: 2%;
        transition: transform 0.1s ease-in-out;
        transform-origin: center center;
        /* 让元素中心对齐目标坐标 */

        .el-progress {
          width: 100%;
          height: 100%;
          position: relative;

          :deep(.el-progress-circle) {
            width: 100% !important;
            height: 100% !important;
          }

          :deep(.el-progress__text) {
            min-width: 0;
            width: 100%;
            height: 100%;

          }

          &.is-exception {

            .card-level {
              background-color: var(--el-color-danger);
            }
          }
        }

        .progress-name {
          font-size: 10px;
          word-break: break-word;
          white-space: pre-wrap;
          padding: 4px;
          overflow: hidden;
          width: 100%;
          height: 100%;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .card-icon,
        .card-level {
          font-size: 10px;
          position: absolute;
          width: 14px;
          height: 14px;
          display: flex;
          justify-content: center;
          align-items: center;
          text-shadow: 0 0 2px #000;
        }

        .card-level {
          top: -2px;
          left: -2px;
          color: white;
          border-radius: 50%;
          background-color: var(--el-color-primary);
        }

        .card-icon {
          font-size: 12px;
          right: -2px;
          bottom: -2px;
        }
      }
    }
  }

}

.hand-card-area {
  padding: 10px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 3;

  .next-card {
    flex: 1;
    padding: 4px;
    background-color: #333;
    border-radius: 4px 0 0 4px;
    overflow: hidden;
    pointer-events: none;
  }

  .card {
    width: 100%;
    // aspect-ratio: 0.8;
    background-color: #666;
    border-radius: 4px;
    // overflow: hidden;
    white-space: pre-wrap;
    word-break: break-all;
    cursor: pointer;
    transition: transform 0.1s ease-in-out;

    &>* {
      pointer-events: none;
    }

    &.select {
      transform: translateY(-10px);
    }
  }

  .current-card {
    flex: 5;
    gap: 8px;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 4px;
    background-color: #333;
    border-radius: 4px;
  }




  .energy {
    padding: 8px;
    text-align: center;

    div {
      font-weight: bold;
      font-size: 18px;
    }
  }

}

.temp-chess {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  transform: translate(var(--x), var(--y));
  width: 20px;
  height: 20px;
  background-color: #000;
  border-radius: 50%;
  border: 1px solid #eee;
  z-index: 2;
  text-align: center;
  color: #fff;
  line-height: 20px;
  font-size: 14px;

  &.show {
    display: block;
  }
}

.info-wrap {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  .info-result {
    font-size: 24px;
    font-weight: bold;
    line-height: 40px;
    margin-bottom: 20px;
    text-align: center;
  }
}

.grid-highlight {
  position: absolute;
  pointer-events: none;
  z-index: 2;
  box-shadow: 0 0 10px 0 #ff0000 inset;

  &.valid {

    box-shadow: 0 0 10px 0 #ffff00 inset;
  }
}
</style>
<style lang="scss" scoped>
.battle-intro {
  background-color: transparent;
  animation: fadeOut 0.8s 1.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) forwards;
}
</style>
<style>
@keyframes attack-line-fade {
  0% {
    opacity: 0;
  }

  30% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.attack-line-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 5;
}
</style>

<style>
/* 闪电打击 - 快速闪烁蓝白光 */
.lightning {
  animation: lightningBlink 0.4s steps(2, start);
}

@keyframes lightningBlink {

  0%,
  50%,
  100% {
    filter: brightness(1);
    box-shadow: none;
  }

  25%,
  75% {
    filter: brightness(1.6) contrast(1.2);
    box-shadow: 0 0 12px 4px rgba(100, 200, 255, 0.9);
  }
}

/* AOE 范围命中 - 橙色脉冲圈（用伪元素） */
.aoe-hit {
  position: relative;
}

.aoe-hit::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 120, 0, 0.8), transparent 70%);
  transform: translate(-50%, -50%);
  animation: aoePulse 0.6s ease-out forwards;
  pointer-events: none;
  z-index: 5;
}

/* dieBoom 爆炸中心 - 更大更红的圈 */
.exploded {
  position: relative;
}

.exploded::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 30, 30, 0.9), transparent 70%);
  transform: translate(-50%, -50%);
  animation: explosionPulse 0.7s ease-out forwards;
  pointer-events: none;
  z-index: 5;
}

.exploded-ice {
  position: relative;
}

.exploded-ice::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(130, 130, 255, 0.9), transparent 70%);
  transform: translate(-50%, -50%);
  animation: explosionPulse 0.7s ease-out forwards;
  pointer-events: none;
  z-index: 5;
}

@keyframes aoePulse {
  0% {
    width: 0;
    height: 0;
    opacity: 0.9;
  }

  100% {
    width: 120px;
    height: 120px;
    opacity: 0;
  }
}

@keyframes explosionPulse {
  0% {
    width: 0;
    height: 0;
    opacity: 1;
  }

  100% {
    width: 160px;
    height: 160px;
    opacity: 0;
  }
}
</style>